import React, { useCallback, useEffect, useRef } from "react"

import { useTableFocusSafe } from "@/human-feedback/focus"
import { Button } from "@coval/ui/button"
import { ChevronDownIcon } from "@coval/ui/chevron-down-icon"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@coval/ui/dropdown-menu"

import { isKeyboardTypingTarget } from "@/lib/isKeyboardTypingTarget"
import { cn } from "@/utils/cn"

interface CategoricalAnnotationSelectorProps {
  selectedValue?: string | null
  onValueChange: (value: string | null) => void
  metricId: string
  categories: string[]
  categoryColors?: Record<string, string>
  readOnly?: boolean
}

export const CategoricalAnnotationSelector: React.FC<
  CategoricalAnnotationSelectorProps
> = ({
  selectedValue,
  onValueChange,
  metricId,
  categories,
  categoryColors,
  readOnly = false,
}) => {
  const tableFocus = useTableFocusSafe()
  const rowRef = useRef<HTMLTableRowElement | null>(null)
  const selfRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  // Register a no-op callback to suppress the table-level Y/N/X key handler
  // (AnnotationTable.tsx) for this metric. Without this, pressing Y/N/X while a
  // categorical metric is focused would call e.preventDefault() with no matching
  // callback, or worse, a stale binary handler. The no-op ensures the keypress
  // is consumed harmlessly while number-key selection (below) handles input.
  useEffect(() => {
    if (!tableFocus || readOnly) {
      return
    }

    const suppressBinaryKeys = () => {}

    tableFocus.registerValueChangeCallback(metricId, suppressBinaryKeys)
    return () => tableFocus.unregisterValueChangeCallback(metricId)
  }, [metricId, readOnly, tableFocus])

  // Capture the closest <tr> ancestor from this component's own DOM node
  useEffect(() => {
    rowRef.current = selfRef.current?.closest(
      "tr",
    ) as HTMLTableRowElement | null
  }, [])

  // Register number key shortcuts for category selection
  useEffect(() => {
    if (!tableFocus || readOnly) {
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isKeyboardTypingTarget(e.target)) {
        return
      }

      if (e.metaKey || e.ctrlKey || e.altKey) {
        return
      }

      // Restrict to the exact row this component belongs to
      const activeElement = document.activeElement as HTMLElement
      const currentRow = activeElement?.closest("tr")

      if (currentRow) {
        // A row is focused — only handle if it's this component's row
        if (currentRow !== rowRef.current) {
          return
        }
      } else {
        // No row DOM-focused — fall back to URL-synced selectedMetricId
        if (tableFocus.selectedMetricId !== metricId) {
          return
        }
        // Verify our row actually exists in the DOM before handling
        if (!rowRef.current) {
          return
        }
      }

      // Enter key opens the dropdown
      if (e.key === "Enter") {
        e.preventDefault()
        triggerRef.current?.click()
        return
      }

      // Number keys 1-9 to select categories
      const num = parseInt(e.key, 10)
      if (num >= 1 && num <= 9 && num <= categories.length) {
        e.preventDefault()
        const category = categories[num - 1]
        if (selectedValue === category) {
          onValueChange(null)
        } else {
          onValueChange(category)
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [tableFocus, metricId, categories, selectedValue, onValueChange, readOnly])

  const handleSelect = useCallback(
    (category: string) => {
      if (readOnly) {
        return
      }
      if (selectedValue === category) {
        onValueChange(null)
      } else {
        onValueChange(category)
      }
    },
    [selectedValue, onValueChange, readOnly],
  )

  return (
    <div ref={selfRef} className="flex items-center justify-center">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            ref={triggerRef}
            aria-label={
              selectedValue
                ? `Selected category ${selectedValue}`
                : "Select category"
            }
            variant="ghost"
            disabled={readOnly}
            className={cn(
              "min-w-[120px] justify-between gap-2 h-7 text-xs px-2 text-coval-text",
              selectedValue && categoryColors?.[selectedValue]
                ? cn("font-medium", categoryColors[selectedValue])
                : "",
            )}
          >
            <span className="flex-1 text-left">
              {selectedValue || "Select"}
            </span>
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent aria-label="Select category">
          {categories.map((category, index) => (
            <DropdownMenuItem
              key={category}
              onSelect={() => handleSelect(category)}
            >
              <div className="flex items-center gap-2 w-full">
                <span>{category}</span>
                {index + 1 <= 9 && (
                  <kbd className="ml-auto px-1 py-0.5 bg-surface-0 border border-coval-border rounded text-xs font-mono text-coval-text-muted">
                    {index + 1}
                  </kbd>
                )}
              </div>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
